import React, { useState, useEffect, useCallback } from 'react';
import {
  Star, MessageSquare, BookOpen, Users, BarChart3, ChevronDown, ChevronUp,
} from 'lucide-react';
import apiClient from '../../utils/apiClient';
import {
  PageHeader, AppCard, AppCardContent,
  EmptyState, AppBadge,
} from '../ui';
import { Table, Spinner } from 'react-bootstrap';
import CommonToaster from '../Common/CommonToaster';
import './AdminFeedbackPage.scss';

/* ─── Avg Star Display ─── */
function AvgStars({ value, label }) {
  const v = value ? parseFloat(value).toFixed(1) : '—';
  const filled = Math.round(value || 0);
  return (
    <div className="afb-avg-item">
      <div className="afb-avg-stars">
        {[1, 2, 3, 4, 5].map((s) => (
          <Star key={s} size={14} className={s <= filled ? 'afb-avg-star--filled' : 'afb-avg-star--empty'} />
        ))}
        <span className="afb-avg-value">{v}</span>
      </div>
      <span className="afb-avg-label">{label}</span>
    </div>
  );
}

/* ─── Expandable Feedback Row ─── */
function FeedbackRow({ fb }) {
  const [expanded, setExpanded] = useState(false);

  const miniStars = (val) => {
    if (!val) return <span className="afb-no-rating">—</span>;
    return (
      <span className="afb-mini-stars">
        {[1, 2, 3, 4, 5].map((s) => (
          <Star key={s} size={12} className={s <= val ? 'afb-avg-star--filled' : 'afb-avg-star--empty'} />
        ))}
      </span>
    );
  };

  const courseAvg = (
    ((fb.courseContent || 0) + (fb.coursePractice || 0) + (fb.courseRelevance || 0) + (fb.courseDifficulty || 0)) / 4
  ).toFixed(1);

  const hasTrainer = fb.trainerTeachingQuality || fb.trainerClarity || fb.trainerDoubtHandling || fb.trainerEngagement;
  const trainerAvg = hasTrainer
    ? (
        ([fb.trainerTeachingQuality, fb.trainerClarity, fb.trainerDoubtHandling, fb.trainerEngagement]
          .filter(Boolean)
          .reduce((a, b) => a + b, 0)) /
        [fb.trainerTeachingQuality, fb.trainerClarity, fb.trainerDoubtHandling, fb.trainerEngagement].filter(Boolean).length
      ).toFixed(1)
    : null;

  return (
    <>
      <tr className="afb-row" onClick={() => setExpanded(!expanded)}>
        <td>
          <div className="afb-student-name">{fb.student?.name || '—'}</div>
          <div className="afb-student-meta">{fb.student?.email || fb.student?.mobile || ''}</div>
        </td>
        <td>{fb.batch?.name || '—'}</td>
        <td>{fb.course?.name || '—'}</td>
        <td>{fb.instructor?.name || '—'}</td>
        <td className="text-center">
          <AppBadge variant="secondary">{courseAvg}</AppBadge>
        </td>
        <td className="text-center">
          {trainerAvg ? <AppBadge variant="outline">{trainerAvg}</AppBadge> : <span className="afb-no-rating">—</span>}
        </td>
        <td className="afb-date-cell">
          {new Date(fb.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' })}
        </td>
        <td className="text-center">
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </td>
      </tr>
      {expanded && (
        <tr className="afb-detail-row">
          <td colSpan="8">
            <div className="afb-detail">
              <div className="afb-detail__section">
                <h6 className="afb-detail__heading"><BookOpen size={14} /> Course Ratings</h6>
                <div className="afb-detail__grid">
                  <div className="afb-detail__item"><span>Content Quality</span>{miniStars(fb.courseContent)}</div>
                  <div className="afb-detail__item"><span>Practice & Assignments</span>{miniStars(fb.coursePractice)}</div>
                  <div className="afb-detail__item"><span>Industry Relevance</span>{miniStars(fb.courseRelevance)}</div>
                  <div className="afb-detail__item"><span>Difficulty Level</span>{miniStars(fb.courseDifficulty)}</div>
                </div>
              </div>
              {hasTrainer && (
                <div className="afb-detail__section">
                  <h6 className="afb-detail__heading"><Users size={14} /> Trainer Ratings</h6>
                  <div className="afb-detail__grid">
                    <div className="afb-detail__item"><span>Teaching Quality</span>{miniStars(fb.trainerTeachingQuality)}</div>
                    <div className="afb-detail__item"><span>Clarity</span>{miniStars(fb.trainerClarity)}</div>
                    <div className="afb-detail__item"><span>Doubt Handling</span>{miniStars(fb.trainerDoubtHandling)}</div>
                    <div className="afb-detail__item"><span>Engagement</span>{miniStars(fb.trainerEngagement)}</div>
                  </div>
                </div>
              )}
              {fb.suggestions && (
                <div className="afb-detail__section">
                  <h6 className="afb-detail__heading"><MessageSquare size={14} /> Suggestions</h6>
                  <p className="afb-detail__suggestions">{fb.suggestions}</p>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

const AdminFeedbackPage = () => {
  const [loading, setLoading] = useState(true);
  const [feedbacks, setFeedbacks] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Filters
  const [courseFilter, setCourseFilter] = useState('');
  const [batchFilter, setBatchFilter] = useState('');
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);

  // Load filter options
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [cRes, bRes] = await Promise.all([
          apiClient.get('/courses/list', { params: { pageSize: 200 } }),
          apiClient.get('/batches/list', { params: { pageSize: 200 } }),
        ]);
        setCourses((cRes.data.courses || []).map((c) => ({ value: c._id, label: c.name })));
        setBatches((bRes.data.batches || []).map((b) => ({ value: b._id, label: b.name })));
      } catch { /* silent */ }
    };
    loadFilters();
  }, []);

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, pageSize };
      if (courseFilter) params.course = courseFilter;
      if (batchFilter) params.batch = batchFilter;
      const { data } = await apiClient.get('/feedback/list', { params });
      setFeedbacks(data.feedbacks || []);
      setTotal(data.total || 0);
      setStats(data.stats || null);
    } catch {
      setToast({ show: true, message: 'Failed to load feedback.', type: 'error' });
    }
    setLoading(false);
  }, [page, pageSize, courseFilter, batchFilter]);

  useEffect(() => { fetchFeedback(); }, [fetchFeedback]);

  useEffect(() => { setPage(1); }, [courseFilter, batchFilter]);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="afb-page">
      <PageHeader
        icon={MessageSquare}
        title="Feedback & Ratings"
        description={`${total} feedback responses from students`}
      />

      {/* Averages Overview */}
      {stats && (
        <div className="afb-stats-row">
          <AppCard className="afb-stat-card">
            <AppCardContent>
              <div className="afb-stat-card__header">
                <BookOpen size={16} />
                <span>Course Averages</span>
                <AppBadge variant="secondary">{stats.count} responses</AppBadge>
              </div>
              <div className="afb-avg-grid">
                <AvgStars value={stats.avgCourseContent} label="Content" />
                <AvgStars value={stats.avgCoursePractice} label="Practice" />
                <AvgStars value={stats.avgCourseRelevance} label="Relevance" />
                <AvgStars value={stats.avgCourseDifficulty} label="Difficulty" />
              </div>
            </AppCardContent>
          </AppCard>
          <AppCard className="afb-stat-card">
            <AppCardContent>
              <div className="afb-stat-card__header">
                <Users size={16} />
                <span>Trainer Averages</span>
              </div>
              <div className="afb-avg-grid">
                <AvgStars value={stats.avgTrainerTeaching} label="Teaching" />
                <AvgStars value={stats.avgTrainerClarity} label="Clarity" />
                <AvgStars value={stats.avgTrainerDoubtHandling} label="Doubt Handling" />
                <AvgStars value={stats.avgTrainerEngagement} label="Engagement" />
              </div>
            </AppCardContent>
          </AppCard>
        </div>
      )}

      {/* Filters */}
      <div className="afb-filters">
        <select className="afb-filter-select" value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)}>
          <option value="">All Courses</option>
          {courses.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select className="afb-filter-select" value={batchFilter} onChange={(e) => setBatchFilter(e.target.value)}>
          <option value="">All Batches</option>
          {batches.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
        </select>
      </div>

      {/* Table */}
      {loading && (
        <div className="afb-loading"><Spinner animation="border" variant="secondary" /></div>
      )}

      {!loading && feedbacks.length === 0 && (
        <EmptyState
          icon={<BarChart3 />}
          title="No feedback yet"
          description="Feedback submitted by students will appear here."
        />
      )}

      {!loading && feedbacks.length > 0 && (
        <AppCard>
          <AppCardContent className="p-0">
            <div className="table-responsive">
              <Table className="mb-0 afb-table" hover>
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Batch</th>
                    <th>Course</th>
                    <th>Trainer</th>
                    <th className="text-center">Course Avg</th>
                    <th className="text-center">Trainer Avg</th>
                    <th>Date</th>
                    <th className="text-center" />
                  </tr>
                </thead>
                <tbody>
                  {feedbacks.map((fb) => <FeedbackRow key={fb._id} fb={fb} />)}
                </tbody>
              </Table>
            </div>
          </AppCardContent>
        </AppCard>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="afb-pagination">
          <span className="afb-pagination__info">
            Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
          </span>
          <div className="afb-pagination__btns">
            <button className="afb-page-btn" disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
            <span className="afb-page-num">{page} / {totalPages}</span>
            <button className="afb-page-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        </div>
      )}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default AdminFeedbackPage;
